// src/components/PortUsageCard.tsx
import React, { useState, useMemo } from "react";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { Port } from "./Types";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { ServiceUsageChart } from "./ServiceUsageChart";
import { formatDataRate } from "kontrolEdge/helpers/formatDataRate";
import { useAppSelector } from "@redux/hooks";
import { TagsWithPopover } from "kontrolEdge/components/Shared/TagsWithPopover/TagsWithPopover";
type PortUsageCardProps = { port: Port; onClick?: (port: Port) => void };

export function PortUsageCard({ port, onClick }: PortUsageCardProps) {
  const { tenantId, partnerId  } = useAppSelector(state => state.selection);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [limitPercent, setLimitPercent] = useState<number>(100);
  const isViewingAllCustomers = tenantId === partnerId;

  // Sum allocated (just internet for now)
  const allocatedSum = useMemo(
    () =>
      Object.values(port.allocated)
        .filter((v) => v != null)
        .reduce((s, v) => s + (v ?? 0), 0),
    [port.allocated]
  );

  // Incoming / outgoing. Whether a reading exists is tracked separately from
  // its value: coalescing "we could not measure it" to 0 is what made a
  // maintenance window look like a port with no traffic.
  const hasReading =
    port.bandwidthConsumption?.incoming != null ||
    port.bandwidthConsumption?.outgoing != null;
  const incoming = port.bandwidthConsumption?.incoming ?? 0;
  const outgoing = port.bandwidthConsumption?.outgoing ?? 0;
  const consumption = incoming + outgoing;
  const capacity = port.capacity ?? 0;

  // Effective max = allocatedSum * (limitPercent/100)
  const effectiveMax = useMemo(
    () => (allocatedSum * limitPercent) / 100,
    [allocatedSum, limitPercent]
  );

  /**
   * Three states, not two. `actualState` is absent whenever the collector
   * could not be read, and this card used to fold that into "Inactive" in red
   * — a measurement failure rendered as a customer outage. The service lists
   * already make this distinction ("Unknown"); this card was the one that
   * did not.
   */
  const rawState = port.state?.actualState ?? port.state?.operationalState;
  const portState: "up" | "down" | "unknown" =
    rawState === "up" ? "up" : rawState === "down" ? "down" : "unknown";

  const STATE_TAG = {
    up: { label: "Active", variant: "success" as const },
    down: { label: "Inactive", variant: "error" as const },
    unknown: { label: "No data", variant: "default" as const },
  }[portState];

  const customerName = port.customer?.name;
  return (
    // The card opens the port's detail; it can also be kept open as a view (project 13D).
    <div {...viewHref(port?.id ? buildDetailPath("port", { portId: port.id }) : null)} onClick={() => onClick?.(port)}>
    <GenericCard
      isCollapsible={false}
      headerContent={
        <div className="flex-wrap justify-between items-start pr-5">
           <div className="flex-1 pr-4">
            <h3 className="text-md font-bold text-semantic-text-primary leading-tight">
            {isViewingAllCustomers
              ? `(${port?.customer?.name}) Port ${port?.name}`
              : `(${port?.subscriptionId}) Port ${port?.name}`
            }
            </h3>
            <h3 className="font-medium text-semantic-text-secondary">
              Allocated: {formatDataRate(allocatedSum)}
            </h3>
            <h3 className="font-medium text-semantic-text-secondary">
              Capacity: {formatDataRate(capacity)}
            </h3>
          </div>
          <div className="flex flex-col gap-2 mt-5">
            {/* Active/Inactive and Services tags */}
            <div className="flex flex-wrap gap-2 items-stretch">
              {isViewingAllCustomers &&   
                <GenericTag
                  label={customerName}
                  variant="neutral"
                />
              }
              <GenericTag
                label={STATE_TAG.label}
                variant={STATE_TAG.variant}
              />
              <GenericTag
                label={`Services: ${port.servicesCount ?? 1}`}
                variant="neutral"
              />
            </div>
            {/* Tags */}
            <div className="flex items-center">
              <TagsWithPopover tags={port?.tag} maxVisible={2} />
            </div>
          </div>
        </div>
      }
      bodyContent={
        <>
          <ServiceUsageChart
            serviceName="Enterprise Internet"
            allocated={effectiveMax}
            consumption={consumption}
            incomingValue={incoming}
            outgoingValue={outgoing}
            hasReading={hasReading}
          />
        </>
      }
    />
    </div>
  );
}
